import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Badge, Row, Col, Card, Alert, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faCheckCircle, faExternalLinkAlt, faCube, faArrowLeft, faShieldAlt, faSyncAlt } from '@fortawesome/free-solid-svg-icons';

// Mock owned NFTs based on health diagrams from libhacontimig (HealthyMe Pro and HealthSync Pro)
const mockOwnedNfts = [
  {
    id: 'eth-1042',
    name: 'HealthyMe Pro #1042 — Sleep Architecture',
    blockchain: 'Ethereum',
    standard: 'ERC-721',
    tokenId: '1042',
    contractAddress: '0x71C8360f3a22D3795F4D338a0f9b6D14e394A',
    txHash: '0x4a8f9c1b2e3d7a8f9c1b2e3d7a8f9c1b2e3d7a8f',
    ipfsHash: 'QmXoypizjW3WknFiJnKLwHCnL72vedxjQkDDP1mXWo6uco',
    tokenUri: 'https://gateway.pinata.cloud/ipfs/QmXoypizjW3WknFiJnKLwHCnL72vedxjQkDDP1mXWo6uco',
    metricType: 'Monthly Sleep Quality & Circadian Rhythm',
    mintDate: '2026-08-22',
    androidFlavour: 'HealthyMe Pro (GPS)',
    packageId: 'com.linardsl.libhacontimig.hm.finalPro',
    attributes: [
      { trait_type: 'Sleep Efficiency', value: '92%' },
      { trait_type: 'Deep Sleep Duration', value: '1h 48m' },
      { trait_type: 'REM Phase Duration', value: '2h 12m' },
      { trait_type: 'Regularity Score', value: '89 / 100' }
    ],
    diagramSummary: 'Synthesized circadian sleep cycle recorded via Oura Ring Gen 3 and synchronized with private Kubernetes PCD cluster.'
  },
  {
    id: 'eth-2088',
    name: 'HealthSync Pro #2088 — Caloric & Step Pulse',
    blockchain: 'Ethereum',
    standard: 'ERC-721',
    tokenId: '2088',
    contractAddress: '0x71C8360f3a22D3795F4D338a0f9b6D14e394A',
    txHash: '0x9d2c1e4f5a1b9d2c1e4f5a1b9d2c1e4f5a1b9d2c',
    ipfsHash: 'QmZ4tDuvesekSs4qM5ZBKpXiZGun7S2CYtEZRB3DYXkjGx',
    tokenUri: 'https://gateway.pinata.cloud/ipfs/QmZ4tDuvesekSs4qM5ZBKpXiZGun7S2CYtEZRB3DYXkjGx',
    metricType: 'Weekly Activity & Caloric Expenditure',
    mintDate: '2026-09-01',
    androidFlavour: 'HealthSync Pro (HAG)',
    packageId: 'com.linardsl.libhacontimig.finalPro',
    attributes: [
      { trait_type: 'Weekly Step Count', value: '73,480 steps' },
      { trait_type: 'Active Calorie Burn', value: '3,780 kcal' },
      { trait_type: 'Goal Achievement', value: '100% (7/7 days)' },
      { trait_type: 'Peak Cadence', value: '142 spm' }
    ],
    diagramSummary: 'Cross-platform Huawei Health & Samsung Health weekly walking and exercise cadence telemetry.'
  },
  {
    id: 'sui-3150',
    name: 'HealthyMe Pro #3150 — Autonomic Readiness Index',
    blockchain: 'Sui',
    standard: 'Sui Move Object',
    tokenId: '0x3a82...d91c',
    contractAddress: '0x2::healthdash::HealthDiagramNFT',
    txHash: '8G3qNmK9jX2pL4vR7sT1wQ6yZ8aB3cE5',
    ipfsHash: 'QmYwAPJzv5CZsnA625s3Xf2nemtYgPpHdWEz79ojWnPbdG',
    tokenUri: 'https://gateway.pinata.cloud/ipfs/QmYwAPJzv5CZsnA625s3Xf2nemtYgPpHdWEz79ojWnPbdG',
    metricType: 'Daily Readiness & HRV Recovery',
    mintDate: '2026-09-07',
    androidFlavour: 'HealthyMe Pro (SGS)',
    packageId: 'com.linardsl.libhacontimig.hm.finalPro',
    attributes: [
      { trait_type: 'HRV Balance', value: '64 ms' },
      { trait_type: 'Resting Heart Rate', value: '56 bpm' },
      { trait_type: 'Skin Temp Deviation', value: '+0.1°C' },
      { trait_type: 'Readiness Index', value: '89 / 100' }
    ],
    diagramSummary: 'High-frequency autonomic nervous system recovery metrics generated prior to morning alarm sync.'
  }
];

const NftViewerModal = ({ show, onHide, isPcdUser = true }) => {
  const [walletAddress, setWalletAddress] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedNft, setSelectedNft] = useState(null);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [mintNotice, setMintNotice] = useState(null);

  const hasMetaMask = typeof window !== 'undefined' && Boolean(window.ethereum);

  const connectMetaMask = useCallback(async () => {
    setIsConnecting(true);
    try {
      if (hasMetaMask) {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts && accounts.length > 0) {
          setWalletAddress(accounts[0]);
        }
      } else {
        // Fallback to PCD Pro Demo Wallet
        setWalletAddress('0x71C8360f3a22D3795F4D338a0f9b6D14e394A');
      }
    } catch (err) {
      console.warn('MetaMask connection rejected or failed:', err);
    } finally {
      setIsConnecting(false);
    }
  }, [hasMetaMask]);

  const disconnectWallet = () => {
    setWalletAddress(null);
    setSelectedNft(null);
  };

  useEffect(() => {
    if (show && hasMetaMask && window.ethereum?.selectedAddress) {
      setWalletAddress(window.ethereum.selectedAddress);
    }
  }, [show, hasMetaMask]);

  const filteredNfts = mockOwnedNfts.filter(nft => {
    if (activeFilter === 'ETHEREUM') return nft.blockchain === 'Ethereum';
    if (activeFilter === 'SUI') return nft.blockchain === 'Sui';
    return true;
  });

  const handleSimulateMint = () => {
    setMintNotice('Initiating MintNFTTask pipeline: 1) Extracting S3 diagram telemetry -> 2) Pinning to IPFS via Pinata -> 3) Submitting on-chain transaction. Complete!');
    setTimeout(() => setMintNotice(null), 5000);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-warning text-white">
        <Modal.Title className="fw-bold d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCube} />
          <span>Health Diagram NFT Vault (PRO Feature)</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* Wallet Connection Status Bar */}
        <div className="bg-light p-3 rounded-3 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2 shadow-sm">
          <div>
            <span className="text-muted small">MetaMask Web3 Status: </span>
            {walletAddress ? (
              <span className="fw-bold text-success">
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Connected: <code>{`${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}`}</code>
              </span>
            ) : (
              <span className="text-muted">Not Connected</span>
            )}
          </div>
          <div>
            {walletAddress ? (
              <Button variant="outline-danger" size="sm" onClick={disconnectWallet}>
                Disconnect
              </Button>
            ) : (
              <Button
                variant="warning"
                size="sm"
                className="text-white fw-bold shadow-sm"
                onClick={connectMetaMask}
                disabled={isConnecting}
              >
                <FontAwesomeIcon icon={faWallet} className="me-1" />
                {isConnecting ? 'Connecting...' : hasMetaMask ? 'Connect MetaMask' : 'Connect Demo Wallet'}
              </Button>
            )}
          </div>
        </div>

        {/* Warning if MetaMask not detected */}
        {!hasMetaMask && !walletAddress && (
          <Alert variant="info" className="border-0 shadow-sm rounded-3 py-2 small mb-3">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-info" />
            MetaMask extension not detected. You can connect via <strong>PCD Pro Demo Wallet</strong> to test the
            Health Diagram NFT viewing experience seamlessly.
          </Alert>
        )}

        {mintNotice && (
          <Alert variant="success" className="border-0 shadow-sm rounded-3 py-2 small mb-3">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            {mintNotice}
          </Alert>
        )}

        {/* Detail View of an Owned NFT */}
        {selectedNft ? (
          <div className="nft-detail-view">
            <Button
              variant="link"
              className="text-muted p-0 mb-3 text-decoration-none d-flex align-items-center gap-1"
              onClick={() => setSelectedNft(null)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back to Owned NFTs List</span>
            </Button>

            <Row className="g-4">
              <Col md={5}>
                <Card className="border-0 shadow-sm rounded-3 p-3 bg-light text-center h-100">
                  <div className="p-4 bg-white rounded-3 shadow-sm mb-3 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '220px' }}>
                    <FontAwesomeIcon icon={faCube} size="4x" className="text-warning mb-2" />
                    <Badge bg={selectedNft.blockchain === 'Ethereum' ? 'primary' : 'info'} className="mb-2">
                      {selectedNft.blockchain} · {selectedNft.standard}
                    </Badge>
                    <div className="fw-bold small text-dark">{selectedNft.metricType}</div>
                  </div>
                  <div className="d-grid gap-2">
                    <a
                      href={selectedNft.tokenUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-dark btn-sm"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                      View on IPFS (Pinata)
                    </a>
                  </div>
                </Card>
              </Col>

              <Col md={7}>
                <div className="h-100 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Badge bg="warning" text="dark">{selectedNft.androidFlavour}</Badge>
                    <small className="text-muted">Minted: {selectedNft.mintDate}</small>
                  </div>
                  <h4 className="fw-bold mb-2">{selectedNft.name}</h4>
                  <p className="text-muted small mb-3">{selectedNft.diagramSummary}</p>

                  <h6 className="fw-bold small text-secondary">Verified Health Diagram Attributes:</h6>
                  <Row className="g-2 mb-3">
                    {selectedNft.attributes.map(attr => (
                      <Col xs={6} key={attr.trait_type}>
                        <div className="p-2 border rounded bg-white text-center">
                          <div className="text-muted small" style={{ fontSize: '0.72rem' }}>{attr.trait_type}</div>
                          <div className="fw-bold small text-dark">{attr.value}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <div className="mt-auto p-2 bg-light rounded small">
                    <div className="mb-1 text-truncate">
                      <strong>Contract / Object:</strong> <code>{selectedNft.contractAddress}</code>
                    </div>
                    <div className="mb-1 text-truncate">
                      <strong>Tx Hash / Digest:</strong> <code>{selectedNft.txHash}</code>
                    </div>
                    <div className="text-truncate">
                      <strong>IPFS CID:</strong> <code>{selectedNft.ipfsHash}</code>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          /* List of Owned NFTs */
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="font-weight-bold mb-0">Owned Health Diagram NFTs ({filteredNfts.length})</h5>
              <Nav variant="pills" activeKey={activeFilter} onSelect={(k) => setActiveFilter(k)}>
                <Nav.Item>
                  <Nav.Link eventKey="ALL" className="py-1 px-2 small">All Chains</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="ETHEREUM" className="py-1 px-2 small">Ethereum</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="SUI" className="py-1 px-2 small">Sui</Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            <Row className="g-3">
              {filteredNfts.map(nft => (
                <Col md={4} key={nft.id}>
                  <Card
                    className="h-100 shadow-sm border-0 rounded-3 p-3 text-center nft-item-card"
                    style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                    onClick={() => setSelectedNft(nft)}
                  >
                    <div className="mb-2 d-flex justify-content-between align-items-center">
                      <Badge bg={nft.blockchain === 'Ethereum' ? 'primary' : 'info'} style={{ fontSize: '0.65rem' }}>
                        {nft.blockchain}
                      </Badge>
                      <small className="text-muted" style={{ fontSize: '0.68rem' }}>#{nft.tokenId}</small>
                    </div>

                    <div className="p-3 bg-light rounded-3 mb-2">
                      <FontAwesomeIcon icon={faCube} size="2x" className="text-warning mb-1" />
                      <div className="fw-bold small text-truncate">{nft.metricType}</div>
                    </div>

                    <div className="fw-bold small text-dark mb-1 text-truncate" title={nft.name}>
                      {nft.name}
                    </div>
                    <div className="text-muted small mb-2" style={{ fontSize: '0.7rem' }}>
                      {nft.androidFlavour}
                    </div>

                    <Button variant="outline-warning" size="sm" className="mt-auto fw-bold" style={{ fontSize: '0.75rem' }}>
                      Inspect Diagram NFT
                    </Button>
                  </Card>
                </Col>
              ))}
            </Row>

            <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="small text-muted">
                NFT minting is executed directly by HealthSync Pro and HealthyMe Pro companion apps (<code>MintNFTTask</code>) via Infura Web3 and Pinata IPFS.
              </div>
              <Button variant="outline-dark" size="sm" onClick={handleSimulateMint}>
                <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                Simulate Health Mint Task
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NftViewerModal;

